import React, { useLayoutEffect, useRef, useState } from 'react'
import { useHover } from 'react-aria'
import * as R from 'remeda'
import { Box } from '../../../core-components'
import { useElementWidth } from '../../hooks'
import { pillDataPill } from '../../select/pill-select/CorePill'
import { CascadeSelectValue } from '../../select/pill-select/PillSelect'
import { overflowPillMeasurerDataPill } from './OverflowPill'

const GAP_WIDTH = 8
/**
 * We limit the number of visible values to 30 to prevent performance issues while measuring. We
 * realistically don't expect more than 30 values to be displayed at once in a collapsed state.
 */
const MAX_VISIBLE_VALUES = 30

export const useExpandable = <Value extends string | CascadeSelectValue = string>({
  values,
  isExpandable,
  wrapperRef,
  wrapperPadding,
  isOpen,
  isAlwaysCollapsed,
}: {
  values: ReadonlyArray<Value>
  isExpandable: undefined | boolean
  wrapperRef: React.RefObject<HTMLDivElement | null>
  wrapperPadding: number
  isOpen?: boolean
  isAlwaysCollapsed?: boolean
}) => {
  const { hoverProps, isHovered } = useHover({ isDisabled: isAlwaysCollapsed })
  const [visibleValues, setVisibleValues] = useState<Array<Value> | undefined>(undefined)
  const isExpanded = isExpandable && (isHovered || isOpen) && !isAlwaysCollapsed
  const { getWidth: getWrapperWidth } = useElementWidth(wrapperRef)

  const isWrapping = Boolean((!isExpandable || isExpanded) && !isAlwaysCollapsed)

  useLayoutEffect(() => {
    if (isWrapping) {
      return
    }

    const pills = [...(wrapperRef.current?.querySelectorAll(`[data-pill="${pillDataPill}"]`) ?? [])]
    const specialPlusXPill = wrapperRef.current?.querySelector(
      `[data-pill="${overflowPillMeasurerDataPill}"]`
    )

    const plusXWidth = specialPlusXPill?.getBoundingClientRect().width ?? 0
    let totalWidth = plusXWidth + wrapperPadding

    const wrapperWidth = getWrapperWidth() ?? 0

    const visibleValues: Array<Value> = []
    for (let i = 0; i < pills.length; i++) {
      const child = pills[i]
      const width = child.getBoundingClientRect().width
      const totalAfterAdding =
        i === pills.length - 1 ? totalWidth + width - plusXWidth : totalWidth + width + GAP_WIDTH
      if (i === 0 || totalAfterAdding <= wrapperWidth) {
        totalWidth += width + GAP_WIDTH
        if (values[i]) {
          visibleValues.push(values[i])
        }
      } else {
        break
      }
    }
    setVisibleValues(prevValues =>
      R.isDeepEqual(prevValues, visibleValues) ? prevValues : visibleValues
    )
  }, [isWrapping, values, wrapperPadding, wrapperRef, getWrapperWidth])

  const invisibleValuesCount = (() => {
    if (isExpanded || !visibleValues) {
      return 0
    }
    return values.length - visibleValues.length
  })()

  return {
    visibleValues: new Set<Value>(visibleValues ?? []),
    isExpanded,
    isWrapping,
    invisibleValuesCount,
    valuesToRender: isWrapping ? values : values.slice(0, MAX_VISIBLE_VALUES),
    expandableProps: hoverProps,
  }
}

export const Expandable = ({
  children,
  when,
  variant,
}: {
  children: React.ReactNode
  variant: 'regular' | 'nested'
  when?: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { getWidth } = useElementWidth(ref)

  const baseHeight = variant === 'regular' ? 8 : 9
  const width = getWidth()
  return (
    <Box h={when ? baseHeight : undefined} ref={ref}>
      <Box
        zIndex={
          100000 /* high zIndex to ignore accidental overlaps, if you need to display something above create a new stacking context */
        }
        w={width ? `${width}px` : undefined}
        position={when ? 'absolute' : 'static'}
        bg={variant === 'regular' || when ? 'bg/section' : 'transparent'}
        borderRadius={variant === 'nested' ? 0 : 10}
      >
        {children}
      </Box>
    </Box>
  )
}
